// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

declare module "vue-router" {
    interface RouteMeta {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;

        /**
         * Tab the page shows when the address has no ?tab=
         */
        defaultTab?: string;

        /**
         * True for form pages. What the user typed is lost when leaving them,
         * so they are never offered as a page to go back to.
         */
        form?: boolean;
    }
}

const routes: RouteRecordRaw[] = [
    /* General / Home */

    {
        name: "home",
        path: "/",
        component: () => import("@/components/routes/HomePage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    /* Auth */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
    },

    {
        name: "tfa-login",
        path: "/login/tfa",
        component: () => import("@/components/routes/auth/TwoFactorLoginPage.vue"),
    },

    {
        name: "signup",
        path: "/signup",
        component: () => import("@/components/routes/auth/SignupPage.vue"),
    },
    {
        name: "signup-success",
        path: "/signup/success",
        component: () => import("@/components/routes/auth/SignupSuccessPage.vue"),
    },

    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
    },

    {
        name: "tp-login",
        path: "/login/tp/:service",
        component: () => import("@/components/routes/auth/ThirdPartyLogin.vue"),
    },

    {
        name: "tp-signup",
        path: "/signup/tp",
        component: () => import("@/components/routes/auth/ThirdPartySignupPage.vue"),
    },

    /* Account */

    {
        name: "account-settings",
        path: "/account-settings",
        component: () => import("@/components/routes/account/AccountSettingsPage.vue"),
    },

    /* Admin */

    {
        name: "admin",
        path: "/admin",
        component: () => import("@/components/routes/admin/AdministrationPage.vue"),
    },

    {
        name: "admin-groups",
        path: "/admin/groups",
        component: () => import("@/components/routes/admin/user-groups/UserGroupListPage.vue"),
    },

    {
        name: "admin-user",
        path: "/admin/users/:id",
        component: () => import("@/components/routes/admin/UserPage.vue"),
    },

    {
        name: "group-details",
        path: "/admin/groups/:id/",
        component: () => import("@/components/routes/admin/user-groups/GroupDetailsPage.vue"),
    },

    {
        name: "role-details",
        path: "/admin/groups/:id",
        component: () => import("@/components/routes/admin/user-groups/GroupDetailsPage.vue"),
    },

    {
        name: "role-page",
        path: "/admin/role/:id",
        component: () => import("@/components/routes/admin/user-groups/RolePage.vue"),
    },

    /* Data space configuration */

    {
        name: "admin-data-space",
        path: "/admin/data-space",
        component: () => import("@/components/routes/admin/DataSpaceConfigurationPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    /* Data sources */

    {
        name: "data-sources",
        path: "/data-sources",
        alias: ["/data-source"],
        component: () => import("@/components/routes/sources/DataSourcesListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "data-sources-create",
        path: "/data-sources-create",
        component: () => import("@/components/routes/sources/CreateDataSourcePage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "data-source",
        path: "/data-sources/:id",
        alias: ["/data-source/:id"],
        component: () => import("@/components/routes/sources/DataSourcePage.vue"),
    },

    /* Data sinks */

    {
        name: "data-sinks",
        path: "/data-sinks",
        alias: ["/data-sink"],
        component: () => import("@/components/routes/sinks/DataSinksListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "data-sinks-create",
        path: "/data-sinks-create",
        component: () => import("@/components/routes/sinks/CreateDataSinkPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "data-sink",
        path: "/data-sinks/:id",
        alias: ["/data-sink/:id"],
        component: () => import("@/components/routes/sinks/DataSinkPage.vue"),
    },

    /* Data applications */

    {
        name: "data-apps",
        path: "/data-apps",
        component: () => import("@/components/routes/apps/DataAppsListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "data-apps-create",
        path: "/data-apps-create",
        component: () => import("@/components/routes/apps/CreateDataAppPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "data-app",
        path: "/data-apps/:id",
        component: () => import("@/components/routes/apps/DataAppPage.vue"),
    },

    /* App marketplace */

    {
        name: "app-marketplace",
        path: "/app-marketplace",
        component: () => import("@/components/routes/marketplace/MarketplacePage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "app-marketplace-listing",
        path: "/app-marketplace/:provider/apps/:id",
        component: () => import("@/components/routes/marketplace/ListingPage.vue"),
    },

    {
        name: "app-marketplace-publications",
        path: "/app-marketplace/publications",
        redirect: { name: "app-marketplace", query: { tab: "publications" } },
    },

    {
        name: "app-marketplace-publish",
        path: "/app-marketplace/publications/publish",
        component: () => import("@/components/routes/marketplace/PublishPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "app-marketplace-publication",
        path: "/app-marketplace/publications/:id",
        component: () => import("@/components/routes/marketplace/PublicationPage.vue"),
    },

    {
        name: "app-marketplace-moderation",
        path: "/app-marketplace/moderation",
        redirect: { name: "app-marketplace", query: { tab: "moderation" } },
    },

    {
        name: "app-marketplace-moderation-request",
        path: "/app-marketplace/moderation/:id",
        component: () => import("@/components/routes/marketplace/ModerationRequestPage.vue"),
    },

    /* Data pipelines */

    {
        name: "pipelines",
        path: "/pipelines",
        component: () => import("@/components/routes/pipelines/PipelineListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "pipeline-create",
        path: "/pipelines-create",
        component: () => import("@/components/routes/pipelines/CreatePipelinePage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "pipeline",
        path: "/pipelines/:id",
        component: () => import("@/components/routes/pipelines/PipelinePage.vue"),
    },

    {
        name: "execution",
        path: "/pipelines/:pid/executions/:id",
        component: () => import("@/components/routes/pipelines/ExecutionPage.vue"),
    },

    {
        name: "analysis-result",
        path: "/pipelines/:pid/analysis-results/:id",
        component: () => import("@/components/routes/pipelines/AnalysisResultPage.vue"),
    },

    /* Catalogs */

    {
        name: "catalogs",
        path: "/catalogs",
        component: () => import("@/components/routes/catalog/CatalogListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "catalog-create",
        path: "/catalog-create",
        component: () => import("@/components/routes/catalog/CreateCatalogPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "catalog",
        path: "/catalog/:id",
        component: () => import("@/components/routes/catalog/CatalogPage.vue"),
    },

    /* Datasets */

    {
        name: "datasets",
        path: "/datasets",
        component: () => import("@/components/routes/dataset/DatasetListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "dataset-create",
        path: "/dataset-create",
        component: () => import("@/components/routes/dataset/CreateDatasetPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "dataset",
        path: "/dataset/:id",
        component: () => import("@/components/routes/dataset/DatasetPage.vue"),
    },

    /* Negotiations */

    {
        name: "negotiations",
        path: "/negotiations",
        component: () => import("@/components/routes/negotiation/NegotiationListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "negotiation-create",
        path: "/negotiation-create",
        component: () => import("@/components/routes/negotiation/CreateNegotiationPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "negotiation",
        path: "/negotiation/:id",
        component: () => import("@/components/routes/negotiation/NegotiationPage.vue"),
    },

    {
        name: "negotiation-offer",
        path: "/negotiation/:id/offer",
        component: () => import("@/components/routes/negotiation/negotiation-page/NegotiationOfferPage.vue"),
        props: true,
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "negotiation-request",
        path: "/negotiation/:id/request",
        component: () => import("@/components/routes/negotiation/negotiation-page/NegotiationRequestPage.vue"),
        props: true,
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    /* Agreements */

    {
        name: "agreements",
        path: "/agreements",
        component: () => import("@/components/routes/agreement/AgreementsListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "agreement",
        path: "/agreement/:id",
        component: () => import("@/components/routes/agreement/AgreementPage.vue"),
    },

    /* Transfers */

    {
        name: "transfers",
        path: "/transfers",
        component: () => import("@/components/routes/transfer/TransferListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "transfer-create",
        path: "/transfer-create",
        component: () => import("@/components/routes/transfer/CreateTransferPage.vue"),
        meta: {
            form: true,
            sidebarSticky: true,
        },
    },

    {
        name: "transfer",
        path: "/transfer/:id",
        component: () => import("@/components/routes/transfer/TransferPage.vue"),
    },

    /* Notifications */

    {
        name: "notifications",
        path: "/notifications",
        component: () => import("@/components/routes/notifications/NotificationsPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    /* Wallet */

    {
        name: "admin-wallet",
        path: "/admin/wallet/:id",
        component: () => import("@/components/routes/admin/wallet/WalletSettingsPage.vue"),
    },

    /* Clearing house */

    {
        name: "clearing-house",
        path: "/clearing-house",
        component: () => import("@/components/routes/clearing-house/ClearingHousePage.vue"),
        meta: {
            defaultTab: "agreements",
            sidebarSticky: false,
        },
    },

    {
        name: "clearing-house-agreement",
        path: "/clearing-house/agreements/:id",
        component: () => import("@/components/routes/clearing-house/RegisteredAgreementPage.vue"),
    },

    {
        name: "clearing-house-transfer",
        path: "/clearing-house/transfers/:id",
        component: () => import("@/components/routes/clearing-house/RegisteredTransferPage.vue"),
    },

    /* Broker requests */

    {
        name: "broker-requests",
        path: "/catalog-broker-requests",
        component: () => import("@/components/routes/broker-requests/BrokerRequestsPage.vue"),
        meta: {
            defaultTab: "sent",
            sidebarSticky: false,
        },
    },

    {
        name: "broker-request",
        path: "/catalog-broker-requests/sent/:id",
        component: () => import("@/components/routes/broker-requests/sent/BrokerRequestPage.vue"),
    },

    {
        name: "provider-request",
        path: "/catalog-broker-requests/received/:id",
        component: () => import("@/components/routes/broker-requests/received/ProviderRequestPage.vue"),
    },

    /* Attestation requests */

    {
        name: "attestation-requests",
        path: "/attestation-requests",
        component: () => import("@/components/routes/attestation-requests/AttestationRequestsPage.vue"),
        meta: {
            defaultTab: "sent",
            sidebarSticky: false,
        },
    },

    {
        name: "attestation-request-sent",
        path: "/attestation-requests/sent/:id",
        component: () => import("@/components/routes/attestation-requests/sent/AttestationSentRequestPage.vue"),
    },

    {
        name: "attestation-request-received",
        path: "/attestation-requests/received/:id",
        component: () => import("@/components/routes/attestation-requests/received/AttestationReceivedRequestPage.vue"),
    },

    /* Federated Learning */

    {
        name: "fl-projects",
        path: "/fl",
        component: () => import("@/components/routes/fl/FLProjectsListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },
    {
        name: "fl-projects-create",
        path: "/fl/create",
        component: () => import("@/components/routes/fl/FLWizardPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },
    {
        name: "fl-trainings",
        path: "/fl/trainings",
        component: () => import("@/components/routes/fl/FLTrainingsListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },
    {
        name: "fl-training",
        path: "/fl/trainings/:id",
        component: () => import("@/components/routes/fl/FLTrainingPage.vue"),
    },
    {
        name: "fl-project",
        path: "/fl/:id",
        component: () => import("@/components/routes/fl/FLPage.vue"),
    },

    /* Negotiation automation policies */

    {
        name: "negotiation-automation-policies",
        path: "/negotiation-automation/policies",
        component: () => import("@/components/routes/negotiation-automation/NegotiationAutomationPolicyListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "negotiation-automation-policy",
        path: "/negotiation-automation/policies/:id",
        component: () => import("@/components/routes/negotiation-automation/NegotiationAutomationPolicyPage.vue"),
    },

    {
        name: "negotiation-automation-policy-create",
        path: "/negotiation-automation/create-policy",
        component: () => import("@/components/routes/negotiation-automation/NegotiationAutomationCreatePolicyPage.vue"),
        meta: {
            form: true,
        },
    },

    /* Default */

    {
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
    },
];

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
